
import Vue from 'vue';
import { getReaderConfig } from '../api';
import {
  createCatalogChapter,
  createMergedPages,
  loadPdfSectionDocuments,
  PdfCatalogChapter,
  renderPageThumbnail,
} from '../utils/pdf';

const STORAGE_KEY = '20260525SQFLZCZN:catalog-state';

interface CatalogState {
  scrollTop: number;
}

export default Vue.extend({
  name: 'PdfReaderHome',
  data() {
    return {
      loading: true,
      title: '',
      errorText: '',
      chapters: [] as PdfCatalogChapter[],
      activeIndex: -1,
      destroyed: false,
      scrollTimer: 0 as number,
    };
  },
  watch: {
    '$route.fullPath'() {
      this.syncActiveIndex();
      this.queueRestorePosition();
    },
  },
  async mounted() {
    await this.initialize();
  },
  activated() {
    this.syncActiveIndex();
    this.queueRestorePosition();
  },
  deactivated() {
    if (this.scrollTimer) {
      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = 0;
    }
    this.saveCatalogState();
  },
  beforeDestroy() {
    this.destroyed = true;
    if (this.scrollTimer) {
      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = 0;
    }
    this.saveCatalogState();
  },
  methods: {
    async initialize() {
      try {
        const response = await getReaderConfig();
        const config = response.data;
        const sectionDocuments = await loadPdfSectionDocuments(config);
        const mergedPages = createMergedPages(sectionDocuments);
        const chapter = createCatalogChapter(mergedPages);

        this.title = config.title;
        document.title = config.title;

        this.chapters = [chapter];
        this.loading = false;
        this.syncActiveIndex();
        this.queueRestorePosition();

        this.generateThumbnails(sectionDocuments);
      } catch (error) {
        this.loading = false;
        this.errorText = 'PDF 加载失败，请稍后再试';
      }
    },
    getSavedState(): CatalogState {
      const rawValue = sessionStorage.getItem(STORAGE_KEY);

      if (!rawValue) {
        return {
          scrollTop: 0,
        };
      }

      try {
        return JSON.parse(rawValue);
      } catch (error) {
        return {
          scrollTop: 0,
        };
      }
    },
    saveCatalogState() {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          scrollTop: window.scrollY,
        }),
      );
    },
    restoreScrollTop() {
      const { scrollTop } = this.getSavedState();

      if (!scrollTop) {
        return;
      }

      window.scrollTo(0, scrollTop);
    },
    syncActiveIndex() {
      const indexValue = Number(this.$route.query.index);

      if (!Number.isNaN(indexValue) && indexValue >= 0) {
        this.activeIndex = indexValue;
        return;
      }

      const pageValue = Number(this.$route.query.page || 0);

      if (Number.isNaN(pageValue) || pageValue <= 0) {
        this.activeIndex = -1;
        return;
      }

      this.activeIndex = pageValue - 1;
    },
    queueRestorePosition() {
      if (this.loading) {
        return;
      }

      if (this.scrollTimer) {
        window.clearTimeout(this.scrollTimer);
      }

      this.$nextTick(() => {
        this.scrollTimer = window.setTimeout(() => {
          this.restorePosition();
          this.scrollTimer = 0;
        }, 80);
      });
    },
    restorePosition() {
      const selector =
        this.activeIndex >= 0 ? `[data-index="${this.activeIndex}"]` : '';

      if (!selector) {
        this.restoreScrollTop();
        return;
      }

      const activeCard = this.$el.querySelector(selector) as HTMLElement | null;

      if (!activeCard) {
        this.restoreScrollTop();
        return;
      }

      const targetTop =
        activeCard.getBoundingClientRect().top + window.pageYOffset - 120;

      window.scrollTo({
        top: Math.max(targetTop, 0),
        behavior: 'auto',
      });
    },
    handleOpenReader(pageNumber: number) {
      this.activeIndex = pageNumber - 1;
      this.saveCatalogState();
      this.$router.push({
        name: '20260525SQFLZCZN-reader',
        query: {
          page: String(pageNumber),
          index: String(pageNumber - 1),
        },
      });
    },
    async generateThumbnails(sectionDocuments: any[]) {
      for (
        let chapterIndex = 0;
        chapterIndex < this.chapters.length;
        chapterIndex += 1
      ) {
        const chapter = this.chapters[chapterIndex];

        for (
          let pageIndex = 0;
          pageIndex < chapter.pages.length;
          pageIndex += 1
        ) {
          const pageItem = chapter.pages[pageIndex];

          if (this.destroyed) {
            return;
          }

          if (pageItem.thumbnail) {
            continue;
          }

          this.$set(pageItem, 'loading', true);

          try {
            const sectionDocument = sectionDocuments.find(
              (item) => item.type === pageItem.sectionType,
            );

            if (!sectionDocument) {
              throw new Error('Section document not found');
            }

            const thumbnail = await renderPageThumbnail(
              sectionDocument.document,
              pageItem.sectionPageNumber,
            );
            this.$set(pageItem, 'thumbnail', thumbnail);
            this.$set(pageItem, 'failed', false);

            if (pageItem.globalPageNumber - 1 === this.activeIndex) {
              this.queueRestorePosition();
            }
          } catch (error) {
            this.$set(pageItem, 'failed', true);
          }

          this.$set(pageItem, 'loading', false);
          await this.$nextTick();
        }
      }
    },
  },
});
